import React from 'react';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faHistory } from '@fortawesome/pro-light-svg-icons';
import { useIsMobile } from '@shared/hooks/useMobile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

export interface FloatingButtonAction {
  label: string;
  onClick: () => void;
  icon?: any;
  dataTestId?: string;
  showLabel?: boolean; // Nuevo prop para controlar si mostrar texto
}

export interface FloatingButtonsProps {
  // Botones predefinidos
  onHistoryClick?: () => void;
  onCustomActionClick?: () => void;
  customActionLabel?: string;
  customActionIcon?: any;
  // Configuración adicional
  className?: string;
}

export const FloatingButtons = ({
  onHistoryClick,
  onCustomActionClick,
  customActionLabel,
  customActionIcon,
  className = '',
}: FloatingButtonsProps) => {
  const isMobile = useIsMobile();

  // Crear acciones basadas en los props
  const actions: FloatingButtonAction[] = [];

  // Agregar botón de historial si existe
  if (onHistoryClick) {
    actions.push({
      label: 'Historial',
      onClick: onHistoryClick,
      icon: faHistory,
      dataTestId: 'history-button',
      showLabel: false, // Solo icono en desktop
    });
  }

  // Agregar botón personalizado si existe
  if (onCustomActionClick && customActionLabel) {
    actions.push({
      label: customActionLabel,
      onClick: onCustomActionClick,
      icon: customActionIcon,
      dataTestId: 'custom-action-button',
      showLabel: true, // Mostrar texto
    });
  }

  // Renderizado para Mobile - Dropdown con tres puntos
  if (isMobile) {
    return (
      <div className={`absolute top-3 right-4 z-50 ${className}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" data-testid="mobile-floating-menu-trigger">
              <FontAwesomeIcon icon={faEllipsisV} className="!w-4 !h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {actions.map((action, index) => (
              <DropdownMenuItem
                key={index}
                onClick={action.onClick}
                className="cursor-pointer"
                data-testid={action.dataTestId || `mobile-action-${index}`}
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // Renderizado para Desktop - Siempre inline
  return (
    <div className={`flex gap-2 absolute top-3 right-4 z-50 ${className}`}>
      {actions.map((action, index) => (
        <Button
          key={index}
          variant="outline"
          size="sm"
          className={`flex items-center gap-2 ${action.showLabel === false ? 'w-8 h-8 p-0' : ''}`}
          onClick={action.onClick}
          data-testid={action.dataTestId || `inline-action-${index}`}
        >
          {action.icon && <FontAwesomeIcon icon={action.icon} className="!w-3 !h-3 text-icons" />}
          {action.showLabel !== false && action.label}
        </Button>
      ))}
    </div>
  );
};
